import Image from 'next/image';
import { useIntl } from 'react-intl';
import styles from './OrganizerBannerBackground.module.scss';

/**
 * Largest CSS width the cover banner is rendered at, matching the `.root`
 * max-width in OrganizerBannerBackground.module.scss (80rem == 1280px at the
 * default root font size).
 *
 * The cover is uploaded at a finite resolution (the organizer profile spec crop
 * is 1280x220) and rendered with next/image `fill`, so without a bound its CSS
 * width is the whole viewport and object-fit: cover upscales the upload past
 * its native resolution on wider desktops -- the blur customers report. The
 * bound keeps a spec-sized cover at 1:1 instead of stretched, and the `sizes`
 * hint tells Next the same so it never selects a larger-than-needed variant.
 */
export const COVER_MAX_WIDTH_PX = 1280;

export interface OrganizerBannerBackgroundProps {
  coverUrl?: string;
  avatarUrl?: string;
  className: string;
  imageTestId?: string;
}

export function OrganizerBannerBackground({
  coverUrl,
  avatarUrl,
  className,
  imageTestId,
}: OrganizerBannerBackgroundProps) {
  const intl = useIntl();
  const coverTrimmed = coverUrl?.trim() ?? '';
  const avatarTrimmed = avatarUrl?.trim() ?? '';
  const hasCover = coverTrimmed.length > 0;
  const useAvatarAsBackground = !hasCover && avatarTrimmed.length > 0;
  const rootClass = [styles.root, className].join(' ');

  return (
    <div className={rootClass}>
      {hasCover ? (
        <Image
          src={coverTrimmed}
          alt={intl.formatMessage({ id: 'Cover', defaultMessage: 'Cover' })}
          className={styles.coverImage}
          data-testid={imageTestId}
          fill
          sizes={`(max-width: ${COVER_MAX_WIDTH_PX}px) 100vw, ${COVER_MAX_WIDTH_PX}px`}
        />
      ) : useAvatarAsBackground ? (
        <Image
          src={avatarTrimmed}
          alt=""
          aria-hidden="true"
          className={styles.avatarBlur}
          data-testid={imageTestId}
          width={1200}
          height={1200}
        />
      ) : null}
    </div>
  );
}
